import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Switch,
  Tabs,
  Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LockIcon from '@mui/icons-material/Lock';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SecurityIcon from '@mui/icons-material/Security';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import { 
  getSigningCredentials, 
  setDefaultSigningCredential, 
  deleteSigningCredential 
} from '../../services/api';
import CredentialManager from './CredentialManager';

/**
 * Credential Manager Page Component
 * 
 * This component displays all available credentials and allows the user to manage them
 */
const CredentialManagerPage = () => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'select'
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [selectedCredentials, setSelectedCredentials] = useState([]);

  // Fetch all credentials when the component mounts
  useEffect(() => {
    fetchCredentials();
  }, []);

  // Handle opening the create credential dialog
  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setSelectedCredential(null);
    setOpenDialog(true);
  };

  // Handle opening the edit credential dialog
  const handleOpenEditDialog = (credentialId) => {
    setDialogMode('select');
    setSelectedCredential(credentialId);
    setOpenDialog(true);
  };

  // Handle credential selection/creation from dialog
  const handleCredentialSelect = (credentialId) => {
    // Refresh the credential list
    fetchCredentials();
  };

  // Handle closing the dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Handle opening delete confirmation
  const handleOpenDeleteConfirm = (credentialId) => {
    setCredentialToDelete(credentialId);
    setDeleteConfirmOpen(true);
  };
  
  // Toggle view mode between grid and table
  const handleViewModeChange = (event, newValue) => {
    setViewMode(newValue);
  };
  
  // Handle checkbox selection in table view
  const handleCheckboxChange = (credentialId) => {
    setSelectedCredentials(prev => {
      if (prev.includes(credentialId)) {
        return prev.filter(id => id !== credentialId);
      } else {
        return [...prev, credentialId];
      }
    });
  };
  
  // Handle select all checkboxes
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = credentials.map(credential => credential.id);
      setSelectedCredentials(allIds);
    } else {
      setSelectedCredentials([]);
    }
  };
  
  // Batch delete selected credentials
  const handleBatchDelete = () => {
    if (selectedCredentials.length === 0) return;
    
    // Filter out default credentials from the selection
    const nonDefaultCredentials = selectedCredentials.filter(id => {
      const credential = credentials.find(c => c.id === id);
      return credential && !credential.isDefault;
    });
    
    if (nonDefaultCredentials.length === 0) {
      setError("Cannot delete default credentials. Please unselect them and try again.");
      return;
    }
    
    setCredentialToDelete(nonDefaultCredentials);
    setDeleteConfirmOpen(true);
  };

  // Handle closing delete confirmation
  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setCredentialToDelete(null);
  };

  // Handle credential deletion
  const handleDeleteCredential = async () => {
    if (!credentialToDelete) return;

    try {
      // Handle single or batch deletion
      if (Array.isArray(credentialToDelete)) {
        // Batch deletion
        const deletePromises = credentialToDelete.map(id => 
          deleteSigningCredential(id)
        );
        
        await Promise.all(deletePromises);
      } else {
        // Single deletion
        await deleteSigningCredential(credentialToDelete);
      }

      // Refresh the credential list
      fetchCredentials();
      setSelectedCredentials([]);
      handleCloseDeleteConfirm();
      setSuccessMessage(
        Array.isArray(credentialToDelete) 
          ? `${credentialToDelete.length} credentials deleted successfully`
          : 'Credential deleted successfully'
      );
    } catch (err) {
      console.error('Error deleting credential:', err);
      setError(err.message || 'Failed to delete credential');
      handleCloseDeleteConfirm();
    }
  };

  // Fetch credentials function
  const fetchCredentials = async () => {
    try {
      setLoading(true);
      const data = await getSigningCredentials();
      setCredentials(data);
    } catch (err) {
      console.error('Error fetching credentials:', err);
      setError(err.message || 'Failed to fetch credentials');
    } finally {
      setLoading(false);
    }
  };

  // Set credential as default
  const handleSetDefault = async (credentialId) => {
    try {
      await setDefaultSigningCredential(credentialId);
      
      // Refresh the credential list
      fetchCredentials();
      setSuccessMessage('Default credential set successfully');
    } catch (err) {
      console.error('Error setting default credential:', err);
      setError(err.message || 'Failed to set as default');
    }
  };
  
  // Clear error/success messages
  const handleClearError = () => {
    setError(null);
  };
  
  const handleClearSuccess = () => {
    setSuccessMessage(null);
  };

  // Function to get security level color
  const getSecurityLevelColor = (level) => {
    switch (level) {
      case 'High':
        return 'success';
      case 'Enterprise':
        return 'info';
      default:
        return 'warning';
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={handleClearError}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={handleClearSuccess}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Signing Credentials
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {credentials.length > 0 && (
            <Tabs 
              value={viewMode} 
              onChange={handleViewModeChange}
              sx={{ mr: 2 }}
            >
              <Tab 
                icon={<GridViewIcon />} 
                value="grid" 
                aria-label="grid view" 
                sx={{ minWidth: 'auto' }}
              />
              <Tab 
                icon={<ViewListIcon />} 
                value="table" 
                aria-label="table view" 
                sx={{ minWidth: 'auto' }}
              />
            </Tabs>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Add New Credential
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {credentials.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                No credentials found
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                You don't have any signing credentials set up yet. Add your first credential to start signing verifiable credentials.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateDialog}
                sx={{ mt: 2 }}
              >
                Add New Credential
              </Button>
            </Paper>
          ) : viewMode === 'grid' ? (
            /* Grid View */
            <Grid container spacing={3}>
              {credentials.map((credential) => (
                <Grid item xs={12} sm={6} md={4} key={credential.id}>
                  <Card variant={credential.isDefault ? 'elevation' : 'outlined'} 
                        elevation={credential.isDefault ? 3 : 1}
                        sx={{
                          borderColor: credential.isDefault ? 'primary.main' : undefined,
                          position: 'relative',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                    {credential.isDefault && (
                      <Chip
                        label="Default"
                        color="primary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          zIndex: 1
                        }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {credential.type === 'Certificate' ? (
                          <VerifiedUserIcon fontSize="large" color="primary" sx={{ mr: 1 }} />
                        ) : (
                          <LockIcon fontSize="large" color="primary" sx={{ mr: 1 }} />
                        )}
                        <Typography variant="h6" component="div" noWrap>
                          {credential.name}
                        </Typography>
                      </Box>

                      <Typography variant="body2" color="text.secondary" paragraph>
                        {credential.description}
                      </Typography>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        <Chip
                          label={credential.type}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                        <Chip
                          label={credential.securityLevel}
                          size="small"
                          color={getSecurityLevelColor(credential.securityLevel)}
                        />
                      </Box>
                    </CardContent>
                    <Divider />
                    <CardActions sx={{ justifyContent: 'space-between' }}>
                      {!credential.isDefault && (
                        <Button
                          size="small"
                          onClick={() => handleSetDefault(credential.id)}
                        >
                          Set as Default
                        </Button>
                      )}
                      <Box>
                        <Tooltip title="Edit credential">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(credential.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!credential.isDefault && (
                          <Tooltip title="Delete credential">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteConfirm(credential.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            /* Table View */
            <Paper variant="outlined">
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1">
                  {selectedCredentials.length} selected
                </Typography>
                {selectedCredentials.length > 0 && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleBatchDelete}
                  >
                    Delete Selected
                  </Button>
                )}
              </Box>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedCredentials.length > 0 && selectedCredentials.length < credentials.length}
                          checked={selectedCredentials.length === credentials.length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Security Level</TableCell>
                      <TableCell>Default</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {credentials.map((credential) => (
                      <TableRow key={credential.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedCredentials.includes(credential.id)}
                            onChange={() => handleCheckboxChange(credential.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {credential.type === 'Certificate' ? (
                              <VerifiedUserIcon color="primary" sx={{ mr: 1 }} />
                            ) : (
                              <LockIcon color="primary" sx={{ mr: 1 }} />
                            )}
                            {credential.name}
                          </Box>
                        </TableCell>
                        <TableCell>{credential.type}</TableCell>
                        <TableCell>{credential.description}</TableCell>
                        <TableCell>
                          <Chip
                            label={credential.securityLevel}
                            size="small"
                            color={getSecurityLevelColor(credential.securityLevel)}
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            edge="end"
                            checked={credential.isDefault}
                            onChange={() => !credential.isDefault && handleSetDefault(credential.id)}
                            disabled={credential.isDefault}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit credential">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditDialog(credential.id)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!credential.isDefault && (
                            <Tooltip title="Delete credential">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDeleteConfirm(credential.id)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* Create/Edit Credential Dialog */}
      <CredentialManager
        open={openDialog}
        onClose={handleCloseDialog}
        onCredentialSelect={handleCredentialSelect}
        selectedCredential={selectedCredential}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            {Array.isArray(credentialToDelete) ? 
              `Are you sure you want to delete ${credentialToDelete.length} selected credential(s)? ` : 
              'Are you sure you want to delete this credential? '}
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
          <Button onClick={handleDeleteCredential} color="error" variant="contained">
            {Array.isArray(credentialToDelete) ? 'Delete Selected' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CredentialManagerPage;